/**
 * FitStreak typed API (Phase 7). Every shape — input body, path params, resolved
 * value — derives from the SERVER's route defs via the type-only import below
 * (erased at build; no server code reaches the bundle). A server-side shape
 * change breaks this file and its callers at compile time.
 *
 * Hand-rolled entries (same call shapes, def-derived outputs):
 * - the three activity-log POSTs: their input schemas coerce `startTime` with
 *   `z.coerce.date()`, so the def's inferred input is `Date` — but the pages send
 *   the raw datetime-local STRING so the SERVER's zone does the coercion.
 *   Converting client-side would shift the stored instant when browser and
 *   server zones differ.
 * - connectGarmin: the def declares no input schema (`parseJson` stays inline so
 *   the per-user rate limit runs BEFORE body validation).
 */
import { apiFetch, type RouteOutputOf, typedRoute } from "@d12labs/client";
import type { fitstreakRouteDefs } from "../server/routes";

/** Wire body of the three activity-log POSTs' shared optional fields. */
type ActivityLogBase = {
	/** Local datetime string ("YYYY-MM-DDTHH:mm") — coerced in the SERVER's zone. */
	startTime?: string;
	title?: string;
	notes?: string;
	durationSec?: number;
};

export const fitstreakApi = {
	dashboard: typedRoute<typeof fitstreakRouteDefs.dashboard>()(
		"GET",
		"/api/plugins/fitstreak/dashboard",
	),

	listActivities: typedRoute<typeof fitstreakRouteDefs.listActivities>()(
		"GET",
		"/api/plugins/fitstreak/activities",
	),

	// Hand-rolled (z.coerce.date startTime — see module docstring) — def-derived output.
	createActivity: (opts: {
		input: ActivityLogBase & {
			type: "run" | "ride" | "swim" | "hike" | "other";
			distanceM?: number;
			elevationM?: number;
			avgHr?: number;
			calories?: number;
		};
	}): Promise<RouteOutputOf<typeof fitstreakRouteDefs.createActivity>> =>
		apiFetch<RouteOutputOf<typeof fitstreakRouteDefs.createActivity>>(
			"/api/plugins/fitstreak/activities",
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	// Hand-rolled (z.coerce.date startTime — see module docstring) — def-derived output.
	logStrength: (opts: {
		input: ActivityLogBase & {
			protocol: string;
			exercises: Array<{
				name: string;
				sets: Array<{ reps: number; weightKg: number; rpe?: number }>;
			}>;
		};
	}): Promise<RouteOutputOf<typeof fitstreakRouteDefs.logStrength>> =>
		apiFetch<RouteOutputOf<typeof fitstreakRouteDefs.logStrength>>(
			"/api/plugins/fitstreak/activities/strength",
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	// Hand-rolled (z.coerce.date startTime — see module docstring) — def-derived output.
	logMobility: (opts: {
		input: ActivityLogBase & { flexibilityScore: number; focusAreas: string[] };
	}): Promise<RouteOutputOf<typeof fitstreakRouteDefs.logMobility>> =>
		apiFetch<RouteOutputOf<typeof fitstreakRouteDefs.logMobility>>(
			"/api/plugins/fitstreak/activities/mobility",
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	removeActivity: typedRoute<typeof fitstreakRouteDefs.removeActivity>()(
		"DELETE",
		"/api/plugins/fitstreak/activities/:id",
	),

	listRaces: typedRoute<typeof fitstreakRouteDefs.listRaces>()(
		"GET",
		"/api/plugins/fitstreak/races",
	),
	createRace: typedRoute<typeof fitstreakRouteDefs.createRace>()(
		"POST",
		"/api/plugins/fitstreak/races",
	),
	removeRace: typedRoute<typeof fitstreakRouteDefs.removeRace>()(
		"DELETE",
		"/api/plugins/fitstreak/races/:id",
	),

	listPlans: typedRoute<typeof fitstreakRouteDefs.listPlans>()(
		"GET",
		"/api/plugins/fitstreak/plans",
	),
	createPlan: typedRoute<typeof fitstreakRouteDefs.createPlan>()(
		"POST",
		"/api/plugins/fitstreak/plans",
	),

	getPreferences: typedRoute<typeof fitstreakRouteDefs.getPreferences>()(
		"GET",
		"/api/plugins/fitstreak/preferences",
	),
	updatePreferences: typedRoute<typeof fitstreakRouteDefs.updatePreferences>()(
		"PATCH",
		"/api/plugins/fitstreak/preferences",
	),

	badges: typedRoute<typeof fitstreakRouteDefs.badges>()("GET", "/api/plugins/fitstreak/badges"),

	sync: typedRoute<typeof fitstreakRouteDefs.sync>()("POST", "/api/plugins/fitstreak/sync"),

	integrations: typedRoute<typeof fitstreakRouteDefs.integrations>()(
		"GET",
		"/api/plugins/fitstreak/integrations",
	),

	// Hand-rolled (no input schema on the def — rate-limit-before-validation) —
	// def-derived output.
	connectGarmin: (opts: {
		input: { username: string; password: string };
	}): Promise<RouteOutputOf<typeof fitstreakRouteDefs.connectGarmin>> =>
		apiFetch<RouteOutputOf<typeof fitstreakRouteDefs.connectGarmin>>(
			"/api/plugins/fitstreak/integrations/garmin",
			{ method: "POST", body: JSON.stringify(opts.input) },
		),

	disconnectGarmin: typedRoute<typeof fitstreakRouteDefs.disconnectGarmin>()(
		"DELETE",
		"/api/plugins/fitstreak/integrations/garmin",
	),
};

/** The server's response shapes, derived — never redeclared by hand. */

/** Everything the dashboard renders (`GET /dashboard`). */
export type DashboardSummary = Awaited<ReturnType<typeof fitstreakApi.dashboard>>;

/** The `paginated(...)` page returned by `GET /activities`. */
export type ActivityPage = RouteOutputOf<typeof fitstreakRouteDefs.listActivities>;

/** One activity row (item of the activities page). */
export type Activity = ActivityPage["items"][number];

/** Create/log response — the new row plus any badges just unlocked. */
export type LoggedActivity = RouteOutputOf<typeof fitstreakRouteDefs.createActivity>;

/** A goal race row (`GET /races`). */
export type Race = Awaited<ReturnType<typeof fitstreakApi.listRaces>>[number];

/** A prescribed-plan row (`GET /plans`, `POST /plans`). */
export type Plan = Awaited<ReturnType<typeof fitstreakApi.createPlan>>;

/** Per-user preferences row (`GET /preferences`). */
export type Preferences = NonNullable<Awaited<ReturnType<typeof fitstreakApi.getPreferences>>>;

/** Per-source connection status (`GET /integrations`). */
export type IntegrationStatus = Awaited<
	ReturnType<typeof fitstreakApi.integrations>
>["sources"][number];

/** `POST /sync` response — per-source results plus any badges just unlocked. */
export type SyncResult = Awaited<ReturnType<typeof fitstreakApi.sync>>;
